import clsx from "clsx";
import PropTypes from "prop-types";
import { useMemo } from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionHeaderName,
  Badge,
  TextTooltip,
} from "@transfr-inc/dashboard-components";
import { Button } from "@transfr-inc/dashboard-components/forms";

import { useResponsiveStyles } from "@lib/hooks";
import { XAPI_OBJECT_TYPES } from "@models/tracking/event-types";
import { ExpandAccordionEvent } from "@models/tracking/xapi-events";

import { getVisibleItemIds } from "@views/curriculum-resources/utils";
import { DocumentsTable } from "./documents.table";

import "./documents.accordion.scss";

export const DocumentsAccordion = ({
  data,
  levelTitle,
  className,
  product,
  showCount = false,
  tracking,
  onDownload,
  downloadingLevelId,
  hasActiveFilters,
  parentLevelId,
}) => {
  const { id, key, value, items, children, isOpen, description } = data ?? {};
  const { responsiveStyles, containerRef } = useResponsiveStyles();

  const trackingData = useMemo(
    () => ({
      curriculumLevelId: id,
      curriculumLevelParentId: parentLevelId,
    }),
    [id, parentLevelId]
  );

  const isWorksheets = key === "course" && value === "VR SIM Worksheets";
  const isDownloading = downloadingLevelId === id;
  const hasDocuments = (items?.length ?? 0) > 0 || (children?.length ?? 0) > 0;

  const handleDownloadClick = (e) => {
    e.stopPropagation();
    onDownload(id, hasActiveFilters ? getVisibleItemIds(data) : null);
  };

  return (
    <Accordion
      key={`${key}-${id}`}
      className={clsx(
        "documents-accordion",
        className,
        isWorksheets && "worksheets-accordion"
      )}
      ref={containerRef}
      isOpen={isOpen}
      // We don't want to track the expansion if the accordion is opened by default
      onExpand={() =>
        !isOpen &&
        tracking.trackEvent(
          new ExpandAccordionEvent(id, value, XAPI_OBJECT_TYPES.ACCORDION_MENU)
        )
      }
    >
      <AccordionHeader>
        <AccordionHeaderName>
          <span>{value}</span>
          {description && (
            <TextTooltip text={description}>
              <FontAwesomeIcon icon={"fa-regular fa-circle-info"} />
            </TextTooltip>
          )}
          {showCount && <Badge value={items?.length} />}
          {isWorksheets && <Badge className="new-badge" value="New!" />}
        </AccordionHeaderName>
        {onDownload && (
          <TextTooltip text="Download zip of these files">
            <Button
              className="accordion-download-button"
              onClick={handleDownloadClick}
              disabled={isDownloading || !hasDocuments}
            >
              <FontAwesomeIcon
                icon={
                  isDownloading
                    ? ["fa-solid", "spinner"]
                    : ["fa-light", "download"]
                }
                spin={isDownloading}
              />
            </Button>
          </TextTooltip>
        )}
      </AccordionHeader>
      <AccordionBody>
        {items?.length > 0 && (
          <DocumentsTable
            data={items}
            responsiveStyles={responsiveStyles}
            className={clsx(levelTitle && "documents-level-items")}
            product={product}
            tracking={tracking}
            trackingData={trackingData}
          />
        )}
        {children?.map((child) => (
          <DocumentsAccordion
            className="documents-children-accordion"
            key={child.id}
            data={child}
            product={product}
            showCount
            tracking={tracking}
            onDownload={onDownload}
            downloadingLevelId={downloadingLevelId}
            hasActiveFilters={hasActiveFilters}
            parentLevelId={id}
          />
        ))}
      </AccordionBody>
    </Accordion>
  );
};

DocumentsAccordion.propTypes = {
  data: PropTypes.object.isRequired,
  levelTitle: PropTypes.string,
  className: PropTypes.string,
  product: PropTypes.object,
  showCount: PropTypes.bool,
  tracking: PropTypes.object,
  onDownload: PropTypes.func,
  downloadingLevelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  hasActiveFilters: PropTypes.bool,
  parentLevelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
